<script setup lang="ts">
import client from '~/api/client'
import type { components } from '~/api/api'
import { useAuthStore } from '~/store/auth'
import { useWebStore } from '~/store/web'
import { adminCountsVersion } from '~/utils/admin-counts'

const props = defineProps<{ mobile?: boolean }>()
const emit = defineEmits<{ navigate: [] }>()

const route = useRoute()
const router = useRouter()
const webStore = useWebStore()
const authStore = useAuthStore()

const collapsed = useState('wb-sidebar-collapsed', () => false)
const drawerOpen = useState('wb-drawer-open', () => false)

type NavItem = {
  id: string
  name: string
  icon: string
  to: string | null
  requiresWeb?: boolean
  // Short label for the compact client icon-rail (falls back to `name`).
  short?: string
  // Count pill (snippet collections) / attention dot (unread poptávky).
  badge?: number | null
  // Records awaiting the owner (pending submissions / new reservations) —
  // shown in the accent colour instead of the grey total.
  pending?: number
  dot?: boolean
}

const snippetTypes = ref<components['schemas']['SnippetTypeOut'][]>([])

let snippetTypesUid: string | null = null
async function loadSnippetTypes() {
  const uid = authStore.user?.is_admin_role ? null : webStore.selectedWebsite?.uid ?? null
  // Only a web switch clears the list — a same-web refresh (tab focus, record
  // approved) swaps it in place, so the Obsah section doesn't blink.
  if (uid !== snippetTypesUid) snippetTypes.value = []
  snippetTypesUid = uid
  if (!uid) return
  const { data } = await client.GET('/api/web/{uid}/snippet-types/', { params: { path: { uid } } })
  if (data && uid === snippetTypesUid) snippetTypes.value = data
}

watch(
  () => webStore.selectedWebsite?.uid,
  () => loadSnippetTypes(),
  { immediate: true },
)
watch(
  () => authStore.user?.id,
  () => loadSnippetTypes(),
)
// Bumped by the editor after an AI turn that touched the web — the AI can add or
// remove content types, and the "Obsah" section would otherwise show the stale
// list until a full page refresh.
const navRefresh = useState('wb-nav-refresh', () => 0)
watch(navRefresh, () => {
  loadSnippetTypes()
  loadUnreadContacts()
})

const snippetItems = computed<NavItem[]>(() =>
  snippetTypes.value.map((t) => ({
    id: `snippet-${t.slug}`,
    name: t.name,
    icon: 'form',
    to: `/snippets/${t.slug}/`,
    requiresWeb: true,
    badge: t.count ?? null,
    // `pending_count` is new on SnippetTypeOut — cast until api.ts is regenerated.
    pending: (t as { pending_count?: number | null }).pending_count ?? 0,
  })),
)

function badgeTitle(item: NavItem) {
  return item.pending ? `${item.name} — ${item.pending} čeká na vyřízení` : item.name
}

// Unread poptávky → attention dot on the Poptávky item (design 1a).
const unreadContacts = ref(0)

async function loadUnreadContacts() {
  unreadContacts.value = 0
  if (authStore.user?.is_admin_role) return
  const uid = webStore.selectedWebsite?.uid
  if (!uid) return
  const { data } = await client.GET('/api/web/{uid}/contact-submissions/unread-count/', {
    params: { path: { uid } },
  })
  if (data) unreadContacts.value = data.count
}

watch(
  () => webStore.selectedWebsite?.uid,
  () => loadUnreadContacts(),
  { immediate: true },
)
watch(
  () => route.path,
  (p, prev) => {
    // Leaving /contact/ = the user just saw the submissions; refresh the dot.
    if (prev?.startsWith('/contact/')) loadUnreadContacts()
    // Same for a content collection (approved / rejected records).
    if (prev?.startsWith('/snippets/')) loadSnippetTypes()
  },
)

function hasFeature(id: string) {
  const f = webStore.selectedWebsite?.features as Record<string, boolean> | undefined
  if (!f) return true
  return f[id] !== false
}

// Grouping per design "Menu redesign" 1a: Tvorba / Obsah / Web / Provoz,
// account items live in the avatar flyout (rail) or an Účet section (drawer).
const userSections = computed<{ label: string; items: NavItem[] }[]>(() => {
  const tvorba: NavItem[] = []
  if (hasFeature('web_edit'))
    tvorba.push({
      id: 'assistant',
      name: 'Asistent',
      short: 'Asistent',
      icon: 'assistant',
      to: '/web-edit/?mode=split',
      requiresWeb: true,
    })
  if (hasFeature('web_edit'))
    tvorba.push({
      id: 'web-edit',
      name: 'Editor',
      short: 'Editor',
      icon: 'edit',
      to: '/web-edit/?mode=preview',
      requiresWeb: true,
    })

  const webItems: NavItem[] = []
  if (hasFeature('domains'))
    webItems.push({
      id: 'domains',
      name: 'Doména',
      short: 'Doména',
      icon: 'globe',
      to: '/domains/',
      requiresWeb: true,
    })
  if (hasFeature('seo'))
    webItems.push({
      id: 'seo',
      name: 'Vyhledávače',
      short: 'Vyhledá­vače',
      icon: 'seo',
      to: '/seo/',
      requiresWeb: true,
    })
  // Firemní údaje (§3) — contacts/address/opening hours → Google JSON-LD.
  if (hasFeature('business'))
    webItems.push({
      id: 'business',
      name: 'Firma',
      short: 'Firma',
      icon: 'business',
      to: '/firma/',
      requiresWeb: true,
    })
  if (hasFeature('statistics'))
    webItems.push({
      id: 'statistics',
      name: 'Statistiky',
      short: 'Statistiky',
      icon: 'chart',
      to: '/statistics/',
      requiresWeb: true,
    })

  const provoz: NavItem[] = []
  if (hasFeature('contact'))
    provoz.push({
      id: 'contact',
      name: 'Zprávy',
      short: 'Zprávy',
      icon: 'rsvp',
      to: '/contact/',
      requiresWeb: true,
      dot: unreadContacts.value > 0,
    })
  // Per-web billing visibility toggle (Web.show_billing). Default on. Editor
  // members never manage billing (F123, /api/auth/me can_manage_billing).
  if (
    webStore.selectedWebsite?.show_billing !== false &&
    (webStore.selectedWebsite as { can_manage_billing?: boolean } | undefined)?.can_manage_billing !== false
  )
    provoz.push({
      id: 'invoices',
      name: 'Platby a tarif',
      short: 'Platby',
      icon: 'invoice',
      to: '/invoices/',
      requiresWeb: true,
    })
  if (hasFeature('web_edit'))
    provoz.push({
      id: 'history',
      name: 'Historie verzí',
      short: 'Historie',
      icon: 'clock',
      to: '/web-history/',
      requiresWeb: true,
    })

  // Media library (images + documents) sits with the content items — it is
  // managed here, but inserted from the editor.
  const obsah: NavItem[] = [...snippetItems.value]
  if (hasFeature('web_edit'))
    obsah.push({
      id: 'media',
      name: 'Soubory',
      short: 'Soubory',
      icon: 'files',
      to: '/media/',
      requiresWeb: true,
    })

  const sections: { label: string; items: NavItem[] }[] = []
  if (tvorba.length) sections.push({ label: 'Tvorba', items: tvorba })
  if (obsah.length) sections.push({ label: 'Obsah', items: obsah })
  if (webItems.length) sections.push({ label: 'Web', items: webItems })
  if (provoz.length) sections.push({ label: 'Provoz', items: provoz })
  return sections
})

// Account items — avatar flyout on the rail, plain "Účet" section in the drawer.
const accountItems = computed<NavItem[]>(() => [
  { id: 'members', name: 'Uživatelé a přístupy', icon: 'guests', to: '/members/', requiresWeb: true },
  { id: 'password', name: 'Přihlašovací údaje', icon: 'lock', to: '/account/password/' },
  { id: 'terms', name: 'Podmínky a ochrana údajů', icon: 'files', to: '/podminky/' },
])

const supportItem = computed<NavItem | null>(() =>
  hasFeature('chat')
    ? { id: 'chat', name: 'Podpora', short: 'Podpora', icon: 'support', to: '/chat/', requiresWeb: true }
    : null,
)

// Drawer/wide sidebar gets the account items as a regular section.
const userSectionsWithAccount = computed<{ label: string; items: NavItem[] }[]>(() => [
  ...userSections.value,
  {
    label: 'Účet',
    items: [...(supportItem.value ? [supportItem.value] : []), ...accountItems.value],
  },
])

// Avatar flyout (rail).
const accountOpen = ref(false)
const accountWrap = ref<HTMLElement | null>(null)

function onDocClick(e: MouseEvent) {
  if (!accountOpen.value) return
  if (accountWrap.value && !accountWrap.value.contains(e.target as Node)) accountOpen.value = false
}
// Back in the tab = new submissions may have arrived meanwhile (the owner got
// the e-mail, switches over) — refresh the counters, no polling.
function onVisible() {
  if (document.visibilityState !== 'visible') return
  loadSnippetTypes()
  loadUnreadContacts()
}
onMounted(() => {
  document.addEventListener('click', onDocClick, true)
  document.addEventListener('visibilitychange', onVisible)
})
onUnmounted(() => {
  document.removeEventListener('click', onDocClick, true)
  document.removeEventListener('visibilitychange', onVisible)
})
watch(
  () => route.path,
  () => (accountOpen.value = false),
)

const userInitial = computed(() => (authStore.user?.email?.[0] || 'U').toUpperCase())
const accountActive = computed(() => route.path.startsWith('/members/') || route.path.startsWith('/account/'))

// ── Admin menu: accordion groups, each = rich pages first, then the generic
// "Databáze" tables of the same domain (back/api/admin_data.py registry).
// Open state is persisted; the group of the active route is always open.
type NavSection = { id: string; label: string; icon: string; items: NavItem[]; dbItems: NavItem[] }

const dbIndex = ref<{ key: string; plural: string; group: string; count: number | null; rich_page: string }[]>([])
async function loadDbIndex() {
  if (!authStore.user?.is_admin_role) return
  const { adminData } = await import('~/utils/admin-data')
  const { data } = await adminData.index()
  if (data) dbIndex.value = data.resources
}
watch(
  () => authStore.user?.is_admin_role,
  (v) => {
    if (v) loadDbIndex()
  },
  { immediate: true },
)
// Row counters follow creates/deletes made on any admin screen (the fetch
// hook in plugins/admin-counts.client.ts bumps this, debounced).
watch(adminCountsVersion, () => loadDbIndex())

// resource group key → sidebar section id
const DB_GROUP_TO_SECTION: Record<string, string> = {
  weby: 'weby',
  sablony: 'sablony',
  ucty: 'ucty',
  media: 'media',
  ai: 'ai',
  komunikace: 'komunikace',
  obchod: 'obchod',
  fakturace: 'fakturace',
  nastaveni: 'nastaveni',
}

const adminSectionsBase: Omit<NavSection, 'dbItems'>[] = [
  {
    id: 'weby',
    label: 'Weby',
    icon: 'home',
    items: [
      { id: 'a-webs', name: 'Weby', icon: 'home', to: '/admin/webs/' },
      { id: 'a-history', name: 'Historie změn', icon: 'clock', to: '/admin/history/' },
      { id: 'a-domains', name: 'Domény', icon: 'globe', to: '/admin/domains/' },
      { id: 'a-free-domains', name: 'Domény zdarma', icon: 'globe', to: '/admin/free-domains/' },
    ],
  },
  {
    id: 'sablony',
    label: 'Šablony & obsah',
    icon: 'templates',
    items: [
      { id: 'a-templates', name: 'Šablony', icon: 'templates', to: '/admin/templates/' },
      { id: 'a-snippets', name: 'Typy obsahu', icon: 'form', to: '/admin/snippet-types/' },
      { id: 'a-colors', name: 'Barevné palety', icon: 'palette', to: '/admin/colors/' },
      { id: 'a-locales', name: 'Jazyky', icon: 'globe', to: '/admin/locales/' },
      { id: 'a-icons', name: 'Ikony', icon: 'icons', to: '/admin/icons/' },
    ],
  },
  {
    id: 'ucty',
    label: 'Účty',
    icon: 'guests',
    items: [{ id: 'a-users', name: 'Uživatelé', icon: 'guests', to: '/admin/users/' }],
  },
  { id: 'media', label: 'Média', icon: 'files', items: [] },
  {
    id: 'ai',
    label: 'AI & automatizace',
    icon: 'assistant',
    items: [
      { id: 'a-ai-models', name: 'AI modely', icon: 'settings', to: '/admin/ai-models/' },
      { id: 'a-oversight', name: 'Přehled & náklady', icon: 'chart', to: '/admin/oversight/' },
      { id: 'a-claude-code', name: 'Claude Code', icon: 'assistant', to: '/admin/claude-code/' },
    ],
  },
  {
    id: 'komunikace',
    label: 'Komunikace',
    icon: 'rsvp',
    items: [
      { id: 'a-requests', name: 'Poptávky', icon: 'form', to: '/admin/requests/' },
      { id: 'a-feedback', name: 'Zpětná vazba', icon: 'support', to: '/admin/feedback/' },
      { id: 'a-chat', name: 'Chat', icon: 'rsvp', to: '/admin/chat/' },
      { id: 'a-maintenance', name: 'Údržba a oznámení', icon: 'settings', to: '/admin/maintenance/' },
    ],
  },
  {
    id: 'obchod',
    label: 'Obchod',
    icon: 'chart',
    items: [{ id: 'a-leads', name: 'Leady (volání)', icon: 'business', to: '/admin/leads/' }],
  },
  {
    id: 'fakturace',
    label: 'Fakturace',
    icon: 'invoice',
    items: [
      { id: 'a-billing', name: 'Faktury & plány', icon: 'invoice', to: '/admin/billing/' },
      { id: 'a-invoice-templates', name: 'Šablony faktur', icon: 'templates', to: '/admin/invoice-templates/' },
    ],
  },
  {
    id: 'nastaveni',
    label: 'Nastavení',
    icon: 'settings',
    items: [{ id: 'a-cookies', name: 'Cookie lišta', icon: 'settings', to: '/admin/cookie-consent/' }],
  },
  {
    id: 'databaze',
    label: 'Databáze',
    icon: 'database',
    items: [{ id: 'a-data', name: 'Všechny tabulky', icon: 'database', to: '/admin/data/' }],
  },
  {
    id: 'ucet',
    label: 'Účet',
    icon: 'lock',
    items: [{ id: 'password', name: 'Změna hesla', icon: 'lock', to: '/account/password/' }],
  },
]

const adminSections = computed<NavSection[]>(() =>
  adminSectionsBase.map((s) => ({
    ...s,
    dbItems: dbIndex.value
      .filter((r) => DB_GROUP_TO_SECTION[r.group] === s.id)
      .map((r) => ({
        id: `db-${r.key}`,
        name: r.plural,
        icon: 'table',
        to: `/admin/data/${r.key}/`,
        badge: r.count,
      })),
  })),
)

// Menu search (admin): filters items across every group, groups auto-open.
const navQuery = ref('')
const navQueryNorm = computed(() =>
  navQuery.value
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, ''),
)
function matchesQuery(item: NavItem) {
  if (!navQueryNorm.value) return true
  const n = item.name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
  return n.includes(navQueryNorm.value)
}
const adminSectionsFiltered = computed(() =>
  adminSections.value
    .map((s) => ({ ...s, items: s.items.filter(matchesQuery), dbItems: s.dbItems.filter(matchesQuery) }))
    .filter((s) => s.items.length || s.dbItems.length),
)

// Accordion open state — persisted. Default: group of the active route only.
const openGroups = ref<Record<string, boolean>>({})
onMounted(() => {
  try {
    const raw = localStorage.getItem('wb-admin-nav-open')
    if (raw) openGroups.value = JSON.parse(raw)
  } catch {
    /* ignore */
  }
})
function persistOpen() {
  try {
    localStorage.setItem('wb-admin-nav-open', JSON.stringify(openGroups.value))
  } catch {
    /* ignore */
  }
}
function sectionHasActive(s: NavSection) {
  return [...s.items, ...s.dbItems].some((i) => isActive(i))
}
function isOpen(s: NavSection) {
  if (navQueryNorm.value) return true
  if (sectionHasActive(s)) return true
  return !!openGroups.value[s.id]
}
function toggleGroup(s: NavSection) {
  if (sectionHasActive(s) && isOpen(s)) {
    // Active group cannot collapse (the current page would vanish from the menu).
    return
  }
  openGroups.value = { ...openGroups.value, [s.id]: !openGroups.value[s.id] }
  persistOpen()
}
function openOnly(s: NavSection) {
  collapsed.value = false
  openGroups.value = { ...openGroups.value, [s.id]: true }
  persistOpen()
}
const adminFlatSections = computed<{ label: string; items: NavItem[] }[]>(() =>
  adminSections.value.map((s) => ({ label: s.label, items: [...s.items, ...s.dbItems] })),
)

const navSections = computed(() =>
  authStore.user?.is_admin_role ? adminFlatSections.value : userSectionsWithAccount.value,
)

// Compact icon-rail nav (design "Menu redesign" 1a): client desktop only.
// Admin keeps the wide text sidebar (too many items for a rail); mobile uses the drawer.
const isAdmin = computed(() => !!authStore.user?.is_admin_role)
const isRail = computed(() => !props.mobile && !isAdmin.value)

function isDisabled(item: NavItem) {
  return !!item.requiresWeb && !webStore.selectedWebsite
}

function navigate(item: NavItem) {
  if (isDisabled(item) || !item.to) return
  router.push(item.to)
  if (props.mobile) {
    drawerOpen.value = false
    emit('navigate')
  }
}

function isActive(item: NavItem) {
  if (!item.to) return false
  return route.path.startsWith(item.to)
}

// Navigate FIRST, drop the tokens only once we actually left: a leave guard
// (web-edit „rozdělané změny" dialog) may keep the user on the page — or
// publish before leaving — and both need a still-valid session.
async function logout() {
  const failure = await router.push('/auth/login')
  if (failure) return
  authStore.logout()
}

// „Chyba / nápad" in the phone drawer: the modal is rendered by SidebarLayout
// (this drawer unmounts when it closes).
const feedbackOpen = useState('wb-feedback-open', () => false)
function openFeedback() {
  drawerOpen.value = false
  feedbackOpen.value = true
}
</script>

<template>
  <!-- ── Compact icon rail (client desktop) — design "Menu redesign" 1a ── -->
  <aside v-if="isRail" class="wb-rail">
    <div class="wb-rail__logo" title="qCMS"><BrandMark :size="24" color="#ffffff" /></div>
    <nav class="wb-rail__nav">
      <template v-for="(section, si) in userSections" :key="section.label">
        <div v-if="si > 0" class="wb-rail__divider"></div>
        <div class="wb-rail__group">{{ section.label }}</div>
        <button
          v-for="item in section.items"
          :key="item.id"
          :data-spot="'sidebar:' + item.id"
          class="wb-rail__item"
          :class="{ active: isActive(item), disabled: isDisabled(item) }"
          :disabled="isDisabled(item)"
          :title="badgeTitle(item)"
          @click="navigate(item)"
        >
          <NavIcon :icon="item.icon" :size="19" />
          <span class="wb-rail__label">{{ item.short || item.name }}</span>
          <span v-if="item.pending" class="wb-rail__badge wb-rail__badge--pending">{{ item.pending }}</span>
          <span v-else-if="item.badge" class="wb-rail__badge">{{ item.badge }}</span>
          <span v-if="item.dot" class="wb-rail__dot"></span>
        </button>
      </template>
    </nav>

    <button
      v-if="supportItem"
      class="wb-rail__item wb-rail__bottom-item"
      :class="{ active: isActive(supportItem), disabled: isDisabled(supportItem) }"
      :disabled="isDisabled(supportItem)"
      :title="supportItem.name"
      @click="navigate(supportItem)"
    >
      <NavIcon :icon="supportItem.icon" :size="19" />
      <span class="wb-rail__label">{{ supportItem.short }}</span>
    </button>

    <div ref="accountWrap" class="wb-rail__account">
      <div class="wb-rail__divider"></div>
      <button
        class="wb-rail__item wb-rail__avatar-item"
        :class="{ active: accountOpen || accountActive }"
        title="Účet"
        @click="accountOpen = !accountOpen"
      >
        <span class="wb-rail__avatar">{{ userInitial }}</span>
        <span class="wb-rail__label">Účet</span>
      </button>

      <!-- Account flyout — opens next to the rail, bottom-anchored. -->
      <div v-if="accountOpen" class="wb-account-menu">
        <div class="wb-account-menu__header">ÚČET — {{ authStore.user?.email }}</div>
        <button
          v-for="item in accountItems"
          :key="item.id"
          class="wb-account-menu__item"
          :class="{ disabled: isDisabled(item) }"
          :disabled="isDisabled(item)"
          @click="navigate(item)"
        >
          <NavIcon :icon="item.icon" :size="17" />
          {{ item.name }}
        </button>
        <div class="wb-account-menu__divider"></div>
        <button class="wb-account-menu__item wb-account-menu__logout" @click="logout">
          <svg
            width="17"
            height="17"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.6"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
            <polyline points="16 17 21 12 16 7" />
            <line x1="21" y1="12" x2="9" y2="12" />
          </svg>
          Odhlásit se
        </button>
      </div>
    </div>
  </aside>

  <aside
    v-else
    :style="
      mobile
        ? {
            width: '100%',
            minWidth: '0',
            position: 'relative',
            height: '100%',
            background: 'var(--cream-2)',
            borderRight: '1px solid var(--hair)',
            padding: '28px 0',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }
        : {
            width: collapsed ? '76px' : '248px',
            minWidth: collapsed ? '76px' : '248px',
            transition: 'width 200ms ease, min-width 200ms ease',
            background: 'var(--cream-2)',
            borderRight: '1px solid var(--hair)',
            padding: '28px 0',
            display: 'flex',
            flexDirection: 'column',
            position: 'fixed',
            left: '0',
            top: '0',
            height: '100vh',
            zIndex: '50',
            overflow: 'hidden',
          }
    "
  >
    <div :style="{ padding: '0 20px 28px', display: 'flex', alignItems: 'center', gap: '10px' }">
      <div
        style="
          width: 36px;
          height: 36px;
          border-radius: 10px;
          background: var(--accent);
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          cursor: pointer;
        "
        @click="collapsed = !collapsed"
      >
        <BrandMark :size="22" color="#ffffff" />
      </div>
      <div v-if="!collapsed || mobile" style="overflow: hidden">
        <div
          style="
            font-family: var(--sans);
            font-weight: 700;
            letter-spacing: -0.015em;
            font-size: 17px;
            color: var(--ink);
            white-space: nowrap;
          "
        >
          qCMS
        </div>
        <div class="wb-mono" style="font-size: 9px; margin-top: 1px">
          {{ authStore.user?.is_admin_role ? 'Admin' : 'Klient' }}
        </div>
      </div>
    </div>

    <!-- ── Admin: accordion groups + menu search (rich pages, then DB tables) ── -->
    <nav v-if="isAdmin" class="adm-nav" :class="{ collapsed: collapsed && !mobile }">
      <div v-if="!collapsed || mobile" class="adm-nav__search">
        <input v-model="navQuery" class="adm-nav__search-input" placeholder="Hledat v menu…" />
        <button v-if="navQuery" type="button" class="adm-nav__search-x" @click="navQuery = ''">×</button>
      </div>

      <!-- collapsed rail: one icon per group, click expands + opens it -->
      <template v-if="collapsed && !mobile">
        <button
          v-for="s in adminSections"
          :key="s.id"
          class="adm-nav__rail-btn"
          :class="{ active: sectionHasActive(s) }"
          :title="s.label"
          @click="openOnly(s)"
        >
          <NavIcon :icon="s.icon" :size="17" />
        </button>
      </template>

      <template v-else>
        <div v-for="s in adminSectionsFiltered" :key="s.id" class="adm-nav__group" :class="{ open: isOpen(s) }">
          <button type="button" class="adm-nav__group-head" @click="toggleGroup(s)">
            <NavIcon :icon="s.icon" :size="15" />
            <span class="adm-nav__group-label">{{ s.label }}</span>
            <span class="adm-nav__group-count">{{ s.items.length + s.dbItems.length }}</span>
            <span class="adm-nav__chev" :class="{ open: isOpen(s) }">›</span>
          </button>
          <div v-show="isOpen(s)" class="adm-nav__items">
            <button
              v-for="item in s.items"
              :key="item.id"
              :data-spot="'sidebar:' + item.id"
              class="adm-nav__item"
              :class="{ active: isActive(item) }"
              @click="navigate(item)"
            >
              <NavIcon :icon="item.icon" :size="15" />
              <span class="adm-nav__item-name">{{ item.name }}</span>
            </button>
            <template v-if="s.dbItems.length">
              <div v-if="s.items.length" class="adm-nav__sub">tabulky</div>
              <button
                v-for="item in s.dbItems"
                :key="item.id"
                class="adm-nav__item adm-nav__item--db"
                :class="{ active: isActive(item) }"
                :title="`${item.name} — ${item.badge ?? '?'} řádků`"
                @click="navigate(item)"
              >
                <NavIcon :icon="item.icon" :size="14" />
                <span class="adm-nav__item-name">{{ item.name }}</span>
                <span v-if="item.badge !== null && item.badge !== undefined" class="adm-nav__badge">{{
                  item.badge
                }}</span>
              </button>
            </template>
          </div>
        </div>
        <div v-if="navQuery && !adminSectionsFiltered.length" class="adm-nav__empty">Nic nenalezeno</div>
      </template>
    </nav>

    <nav v-else style="flex: 1; overflow-y: auto; padding: 0 8px">
      <div v-for="(section, si) in navSections" :key="si" style="margin-bottom: 20px">
        <div v-if="!collapsed || mobile" class="wb-mono" style="padding: 0 8px 8px; font-size: 9px">
          {{ section.label }}
        </div>
        <button
          v-for="item in section.items"
          :key="item.id"
          :data-spot="'sidebar:' + item.id"
          @click="navigate(item)"
          :disabled="isDisabled(item)"
          :style="{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: collapsed && !mobile ? '10px' : '9px 12px',
            justifyContent: collapsed && !mobile ? 'center' : 'flex-start',
            border: 'none',
            background: isActive(item) ? 'var(--paper)' : 'transparent',
            borderRadius: 'var(--r-md)',
            color: isActive(item) ? 'var(--ink)' : isDisabled(item) ? 'var(--ink-3)' : 'var(--ink-2)',
            fontSize: '13px',
            fontWeight: isActive(item) ? '500' : '400',
            cursor: isDisabled(item) ? 'not-allowed' : 'pointer',
            marginBottom: '2px',
            boxShadow: isActive(item) ? '0 1px 2px rgba(31,27,22,0.04)' : 'none',
            fontFamily: 'var(--sans)',
            opacity: isDisabled(item) ? '0.4' : '1',
            transition: 'all 120ms ease',
          }"
        >
          <NavIcon :icon="item.icon" :size="16" />

          <template v-if="!collapsed || mobile">
            <span style="flex: 1; text-align: left; white-space: nowrap; overflow: hidden; text-overflow: ellipsis">
              {{ item.name }}
            </span>
            <span v-if="item.pending" class="wb-mono wb-nav-pending" :title="badgeTitle(item)">
              {{ item.badge }} · {{ item.pending }} čeká
            </span>
            <span v-else-if="item.badge" class="wb-mono" style="font-size: 10px; color: var(--ink-3)">
              {{ item.badge }}
            </span>
            <span
              v-if="item.dot"
              style="width: 6px; height: 6px; border-radius: 50%; background: var(--accent); flex-shrink: 0"
            ></span>
          </template>
        </button>
      </div>
    </nav>

    <div
      v-if="!authStore.user?.is_admin_role && webStore.selectedWebsite && (!collapsed || mobile)"
      style="padding: 10px 14px 0"
    >
      <span
        v-if="(webStore.selectedWebsite as any).has_domain"
        class="wb-status"
        style="width: 100%; justify-content: center; font-size: 12px"
        >Web je online</span
      >
      <NuxtLink
        v-else
        to="/domains/"
        class="wb-status wb-status--draft"
        style="width: 100%; justify-content: center; font-size: 12px"
        title="Web zatím nemá veřejnou adresu — klikněte a nastavte ji"
        >Bez adresy — nastavit</NuxtLink
      >
    </div>
    <DeployChip :collapsed="collapsed && !mobile" />
    <!-- Phone: the drawer is the only menu on /web-edit (no toolbar there) — QA F055. -->
    <div v-if="mobile" style="padding: 12px 14px 0">
      <button type="button" class="sn-feedback" @click="openFeedback">
        <svg
          width="15"
          height="15"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
        >
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          <path d="M12 7v4" />
          <path d="M12 14h.01" />
        </svg>
        Chyba / nápad
      </button>
    </div>
    <div style="padding: 12px 14px; border-top: 1px solid var(--hair)">
      <button
        @click="logout"
        :style="{
          width: '100%',
          padding: collapsed && !mobile ? '10px' : '9px 12px',
          border: '1px solid var(--hair)',
          background: 'var(--paper)',
          borderRadius: 'var(--r-md)',
          fontFamily: 'var(--sans)',
          fontSize: '13px',
          color: 'var(--ink-2)',
          cursor: 'pointer',
        }"
      >
        {{ collapsed && !mobile ? '×' : 'Odhlásit se' }}
      </button>
    </div>
  </aside>
</template>

<style scoped>
.sn-feedback {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 9px 12px;
  border: 1px solid var(--hair);
  background: var(--paper);
  border-radius: var(--r-md);
  font-family: var(--sans);
  font-size: 13px;
  color: var(--ink-2);
  cursor: pointer;
}

.wb-rail {
  position: fixed;
  left: 0;
  top: 0;
  height: 100vh;
  width: 84px;
  z-index: 50;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  background: var(--paper);
  border-right: 1px solid var(--hair);
  padding: 14px 8px;
}
.wb-rail__logo {
  width: 40px;
  height: 40px;
  margin: 0 auto 14px;
  border-radius: 11px;
  background: var(--accent);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.wb-rail__nav {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 4px;
  scrollbar-width: thin;
  /* Scroll shadows: a soft edge appears at the top/bottom only while there is
     more menu that way (the "local" layers cover the shadow at the ends). */
  background:
    linear-gradient(var(--paper) 30%, transparent) top / 100% 28px no-repeat local,
    linear-gradient(transparent, var(--paper) 70%) bottom / 100% 28px no-repeat local,
    radial-gradient(farthest-side at 50% 0, rgba(31, 27, 22, 0.16), transparent) top / 100% 10px no-repeat scroll,
    radial-gradient(farthest-side at 50% 100%, rgba(31, 27, 22, 0.16), transparent) bottom / 100% 10px no-repeat scroll;
}
/* Items never shrink: in the flex column they used to squash (and clip their
   labels — „Do…", an envelope without „Zprávy") instead of letting the nav
   scroll when 5+ content types don't fit the window height. */
.wb-rail__nav > * {
  flex-shrink: 0;
}
@media (max-height: 940px) {
  .wb-rail__nav .wb-rail__item {
    padding: 8px 4px;
  }
}
.wb-rail__item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
  padding: 11px 4px;
  border: none;
  border-radius: 11px;
  background: transparent;
  color: var(--ink-3);
  cursor: pointer;
  transition: all 0.15s ease;
  font-family: var(--sans);
}
.wb-rail__item:hover:not(.disabled):not(.active) {
  background: var(--cream-2);
  color: var(--ink-2);
}
.wb-rail__item.active {
  background: #dfe3f5;
  color: var(--accent);
}
.wb-rail__item.disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
.wb-rail__label {
  max-width: 100%;
  font-size: 10px;
  font-weight: 600;
  line-height: 1.2;
  text-align: center;
  overflow: hidden;
  /* Long labels (Vyhledávače) break on a soft hyphen onto a second line. */
  white-space: normal;
  overflow-wrap: break-word;
}
.wb-rail__group {
  font-family: var(--mono, 'IBM Plex Mono', monospace);
  font-size: 8.5px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  text-align: center;
  color: var(--ink-3);
  padding: 4px 0 5px;
  flex-shrink: 0;
}
.wb-rail__divider {
  width: 40px;
  height: 1px;
  background: var(--hair);
  margin: 8px auto 4px;
  flex-shrink: 0;
}
.wb-rail__item {
  position: relative;
}
.wb-rail__badge {
  position: absolute;
  top: 5px;
  right: 7px;
  font-family: var(--mono, 'IBM Plex Mono', monospace);
  font-size: 9px;
  color: var(--ink-3);
  background: var(--cream-2);
  border-radius: 20px;
  padding: 1px 5px;
  line-height: 1.4;
}
.wb-rail__badge--pending {
  color: #fff;
  background: var(--accent);
  font-weight: 600;
}
.wb-nav-pending {
  font-size: 10px;
  color: var(--accent);
  font-weight: 600;
  white-space: nowrap;
}
.wb-rail__dot {
  position: absolute;
  top: 8px;
  right: 12px;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--accent);
}
.wb-rail__bottom-item {
  flex-shrink: 0;
  margin-top: 6px;
}
.wb-rail__account {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: stretch;
}
.wb-rail__avatar-item {
  padding: 8px 4px;
}
.wb-rail__avatar {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: var(--accent);
  color: var(--paper);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 600;
}
.wb-account-menu {
  position: fixed;
  left: 92px;
  bottom: 14px;
  z-index: 60;
  width: 264px;
  background: var(--paper);
  border: 1px solid var(--hair);
  border-radius: 12px;
  box-shadow: 0 8px 28px rgba(31, 27, 22, 0.12);
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.wb-account-menu__header {
  font-family: var(--mono, 'IBM Plex Mono', monospace);
  font-size: 9px;
  letter-spacing: 0.1em;
  color: var(--ink-3);
  padding: 6px 8px 8px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.wb-account-menu__item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 8px;
  border: none;
  border-radius: 8px;
  background: transparent;
  font-family: var(--sans);
  font-size: 13px;
  color: var(--ink-2);
  cursor: pointer;
  text-align: left;
  transition: background 120ms ease;
}
.wb-account-menu__item:hover:not(.disabled) {
  background: var(--cream-2);
}
.wb-account-menu__item.disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
.wb-account-menu__divider {
  height: 1px;
  background: var(--hair);
  margin: 4px 8px;
}
.wb-account-menu__logout {
  color: var(--accent);
}
/* ── Admin accordion nav ── */
.adm-nav {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 0 8px;
  scrollbar-width: thin;
}
.adm-nav__search {
  position: relative;
  margin: 0 4px 10px;
}
.adm-nav__search-input {
  width: 100%;
  padding: 7px 26px 7px 10px;
  border: 1px solid var(--hair);
  border-radius: var(--r-md);
  background: var(--paper);
  font-family: var(--sans);
  font-size: 12.5px;
  color: var(--ink);
  outline: none;
}
.adm-nav__search-input:focus {
  border-color: var(--blush-deep);
}
.adm-nav__search-x {
  position: absolute;
  right: 6px;
  top: 50%;
  transform: translateY(-50%);
  border: none;
  background: transparent;
  color: var(--ink-3);
  cursor: pointer;
  font-size: 15px;
}
.adm-nav__group {
  margin-bottom: 2px;
}
.adm-nav__group-head {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 8px 10px;
  border: none;
  background: transparent;
  border-radius: var(--r-md);
  color: var(--ink-2);
  font-family: var(--sans);
  font-size: 12.5px;
  font-weight: 600;
  letter-spacing: 0.01em;
  cursor: pointer;
  text-align: left;
}
.adm-nav__group-head:hover {
  background: var(--cream-3);
}
.adm-nav__group.open .adm-nav__group-head {
  color: var(--ink);
}
.adm-nav__group-label {
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.adm-nav__group-count {
  font-family: var(--mono);
  font-size: 9.5px;
  color: var(--ink-3);
}
.adm-nav__chev {
  display: inline-block;
  color: var(--ink-3);
  font-size: 15px;
  line-height: 1;
  transition: transform 140ms ease;
}
.adm-nav__chev.open {
  transform: rotate(90deg);
}
.adm-nav__items {
  padding: 2px 0 8px 6px;
  margin-left: 14px;
  border-left: 1px solid var(--hair);
}
.adm-nav__item {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 6px 10px;
  border: none;
  background: transparent;
  border-radius: var(--r-sm);
  color: var(--ink-2);
  font-family: var(--sans);
  font-size: 12.5px;
  cursor: pointer;
  text-align: left;
  margin-bottom: 1px;
}
.adm-nav__item:hover {
  background: var(--cream-3);
  color: var(--ink);
}
.adm-nav__item.active {
  background: var(--paper);
  color: var(--ink);
  font-weight: 500;
  box-shadow: 0 1px 2px rgba(31, 27, 22, 0.05);
}
.adm-nav__item--db {
  color: var(--ink-3);
  font-size: 12px;
  padding-top: 5px;
  padding-bottom: 5px;
}
.adm-nav__item--db.active {
  color: var(--ink);
}
.adm-nav__item-name {
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.adm-nav__badge {
  font-family: var(--mono);
  font-size: 9.5px;
  color: var(--ink-3);
  background: var(--cream-3);
  border-radius: 999px;
  padding: 1px 6px;
}
.adm-nav__sub {
  font-family: var(--mono);
  font-size: 8.5px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--ink-3);
  padding: 8px 10px 3px;
}
.adm-nav__rail-btn {
  width: 100%;
  display: flex;
  justify-content: center;
  padding: 10px;
  margin-bottom: 2px;
  border: none;
  background: transparent;
  border-radius: var(--r-md);
  color: var(--ink-2);
  cursor: pointer;
}
.adm-nav__rail-btn:hover {
  background: var(--cream-3);
}
.adm-nav__rail-btn.active {
  background: var(--paper);
  color: var(--accent);
}
.adm-nav__empty {
  padding: 10px;
  font-size: 12px;
  color: var(--ink-3);
}
</style>
